import React from 'react';

type LandingPageProps = {
  onGetStarted: () => void;
};

const navLinks = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Features', href: '#features' },
  { label: 'Impact', href: '#impact' },
  { label: 'About', href: '#about' },
  { label: 'Partners', href: '#partners' }
];

const roleCards = [
  {
    icon: '🆘',
    title: 'For Patients',
    description: 'Send SOS, choose your hospital, and track your ambulance live.'
  },
  {
    icon: '🚑',
    title: 'For Ambulance Teams',
    description: 'Get instant dispatch, live navigation, and automatic rerouting.'
  },
  {
    icon: '🏥',
    title: 'For Hospitals',
    description: 'Receive pre-alerts, manage incoming patients, and coordinate beds.'
  }
];

const steps = [
  {
    icon: '📱',
    title: 'Patient sends SOS',
    text: 'A patient triggers an emergency request with hospital preference and location.'
  },
  {
    icon: '🚨',
    title: 'Nearest ambulance notified',
    text: 'The closest available team is alerted instantly with the full context.'
  },
  {
    icon: '🧭',
    title: 'Live GPS routing',
    text: 'Patients, ambulances, and hospitals see updated ETAs and rerouting in real time.'
  },
  {
    icon: '✅',
    title: 'Safe arrival and handoff',
    text: 'The hospital is pre-alerted so the patient enters care without delay.'
  }
];

const featureCards = [
  { icon: '🚑', title: 'Real-time Dispatch', description: 'Coordinate responders instantly with live state updates.' },
  { icon: '🗺️', title: 'Live GPS Tracking + Auto-rerouting', description: 'Keep teams moving with smart route updates and ETA changes.' },
  { icon: '🏥', title: 'Smart Hospital Booking', description: 'Match patients with the right facility in seconds.' },
  { icon: '⌚', title: 'Connected Devices', description: 'Apple Watch and crash detection can trigger automatic SOS.', badge: 'Coming Soon' },
  { icon: '🤖', title: 'Agentic AI', description: 'Coordinate hundreds of responders during mass crises.', badge: 'Coming Soon' },
  { icon: '🌊', title: 'Disaster Mode', description: 'Support flood and disaster response with coordinated field operations.', badge: 'Coming Soon' }
];

const impactStats = [
  { value: '1.8B+', label: 'people face significant flood risk — 23% of humanity', source: 'World Bank, 2022' },
  { value: 'Up to 12%', label: 'survival drops every minute of delay in cardiac arrest' },
  { value: '50%+', label: 'of patients in developing countries miss the golden hour' },
  { value: '19+ min', label: 'typical ambulance response in many developing cities' }
];

export function LandingPage({ onGetStarted }: LandingPageProps) {
  return (
    <div style={{ background: '#f8fafc', color: '#0f172a' }}>
      <style>{`
        html { scroll-behavior: smooth; }
        body { margin: 0; font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        * { box-sizing: border-box; }
        @keyframes pulseGlow {
          0%, 100% { transform: scale(1); opacity: 0.55; }
          50% { transform: scale(1.08); opacity: 0.9; }
        }
      `}</style>

      <nav style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(15, 23, 42, 0.96)', backdropFilter: 'blur(14px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href="#hero" style={{ color: 'white', textDecoration: 'none', fontSize: '1.1rem', fontWeight: 800, letterSpacing: '-0.02em' }}>🚑 UrbanNerve</a>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '0.95rem' }}>{link.label}</a>
            ))}
            <button onClick={onGetStarted} style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: 'white', border: 'none', borderRadius: '999px', padding: '0.7rem 1rem', cursor: 'pointer', fontWeight: 700, boxShadow: '0 8px 24px rgba(37,99,235,0.25)' }}>
              Login
            </button>
          </div>
        </div>
      </nav>

      <main>
        <section id="hero" style={{ position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg, #0f172a 0%, #172554 45%, #111827 100%)', color: 'white' }}>
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: '8%', right: '8%', width: '280px', height: '280px', borderRadius: '50%', background: 'rgba(239, 68, 68, 0.18)', filter: 'blur(10px)', animation: 'pulseGlow 6s ease-in-out infinite' }} />
            <div style={{ position: 'absolute', bottom: '8%', left: '6%', width: '320px', height: '320px', borderRadius: '50%', background: 'rgba(37, 99, 235, 0.2)', filter: 'blur(12px)', animation: 'pulseGlow 8s ease-in-out infinite' }} />
          </div>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '5.5rem 1.25rem 5rem', position: 'relative', zIndex: 1 }}>
            <div style={{ maxWidth: '720px' }}>
              <p style={{ margin: '0 0 1rem', color: '#93c5fd', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.22em', fontSize: '0.82rem' }}>Emergency coordination platform</p>
              <h1 style={{ margin: 0, fontSize: 'clamp(2.7rem, 5.4vw, 4.6rem)', lineHeight: 1.05, letterSpacing: '-0.03em', fontWeight: 900 }}>Every Second Counts</h1>
              <p style={{ margin: '1.2rem 0 0', fontSize: 'clamp(1.05rem, 2vw, 1.35rem)', lineHeight: 1.7, color: 'rgba(255,255,255,0.82)', maxWidth: '660px' }}>
                Real-time emergency coordination connecting patients, ambulances, and hospitals — instantly.
              </p>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap' }}>
                <button onClick={onGetStarted} style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white', border: 'none', borderRadius: '999px', padding: '0.95rem 1.35rem', cursor: 'pointer', fontWeight: 800, fontSize: '1rem', boxShadow: '0 10px 30px rgba(239,68,68,0.24)' }}>
                  Get Started
                </button>
                <a href="#how-it-works" style={{ background: 'rgba(255,255,255,0.07)', color: 'white', border: '1px solid rgba(255,255,255,0.16)', borderRadius: '999px', padding: '0.95rem 1.35rem', textDecoration: 'none', fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>
                  See How It Works
                </a>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginTop: '2.3rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '1rem', padding: '1rem 1.1rem' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 800 }}>24/7</div>
                  <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.72)', marginTop: '0.25rem' }}>Live coordination across every participant</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '1rem', padding: '1rem 1.1rem' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 800 }}>Instant</div>
                  <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.72)', marginTop: '0.25rem' }}>Accelerated dispatch and hospital pre-alerting</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section style={{ background: 'white', padding: '5rem 1.25rem' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.25rem' }}>
              {roleCards.map((card) => (
                <div key={card.title} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '1.25rem', padding: '1.6rem', boxShadow: '0 16px 36px rgba(15,23,42,0.06)', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }} onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 24px 48px rgba(15,23,42,0.1)'; }} onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 16px 36px rgba(15,23,42,0.06)'; }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.7rem' }}>{card.icon}</div>
                  <h3 style={{ margin: '0 0 0.6rem', fontSize: '1.2rem', fontWeight: 800 }}>{card.title}</h3>
                  <p style={{ margin: 0, color: '#475569', lineHeight: 1.7 }}>{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" style={{ background: '#f8fafc', padding: '5rem 1.25rem' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 2.5rem' }}>
              <p style={{ color: '#2563eb', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.6rem' }}>How It Works</p>
              <h2 style={{ margin: 0, fontSize: 'clamp(2rem, 3vw, 2.7rem)', lineHeight: 1.15, letterSpacing: '-0.025em' }}>A faster path from emergency to care</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
              {steps.map((step, index) => (
                <div key={step.title} style={{ background: 'white', borderRadius: '1rem', padding: '1.4rem', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '2.35rem', height: '2.35rem', borderRadius: '999px', background: 'linear-gradient(135deg, #2563eb, #1e3a8a)', color: 'white', fontWeight: 800, marginBottom: '0.9rem' }}>{index + 1}</div>
                  <div style={{ fontSize: '1.4rem', marginBottom: '0.25rem' }}>{step.icon}</div>
                  <h3 style={{ margin: '0 0 0.45rem', fontSize: '1.05rem', fontWeight: 800 }}>{step.title}</h3>
                  <p style={{ margin: 0, color: '#475569', lineHeight: 1.7 }}>{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="features" style={{ background: 'white', padding: '5rem 1.25rem' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 2.5rem' }}>
              <p style={{ color: '#2563eb', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.6rem' }}>Features</p>
              <h2 style={{ margin: 0, fontSize: 'clamp(2rem, 3vw, 2.7rem)', lineHeight: 1.15, letterSpacing: '-0.025em' }}>Built for the speed and complexity of real emergencies</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
              {featureCards.map((card) => (
                <div key={card.title} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '1rem', padding: '1.4rem', position: 'relative' }}>
                  {card.badge && (
                    <span style={{ position: 'absolute', top: '1rem', right: '1rem', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '999px', padding: '0.28rem 0.62rem', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{card.badge}</span>
                  )}
                  <div style={{ fontSize: '1.6rem', marginBottom: '0.5rem' }}>{card.icon}</div>
                  <h3 style={{ margin: '0 0 0.45rem', fontSize: '1.05rem', fontWeight: 800 }}>{card.title}</h3>
                  <p style={{ margin: 0, color: '#475569', lineHeight: 1.7 }}>{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="impact" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #111827 100%)', color: 'white', padding: '5rem 1.25rem' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', maxWidth: '760px', margin: '0 auto 2.5rem' }}>
              <p style={{ color: '#93c5fd', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.6rem' }}>Impact</p>
              <h2 style={{ margin: 0, fontSize: 'clamp(2rem, 3vw, 2.7rem)', lineHeight: 1.15, letterSpacing: '-0.025em' }}>The cost of delay is measured in lives</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              {impactStats.map((stat) => (
                <div key={stat.label} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '1rem', padding: '1.2rem' }}>
                  <div style={{ fontSize: '1.6rem', fontWeight: 900, marginBottom: '0.35rem' }}>{stat.value}</div>
                  <div style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.6 }}>{stat.label}</div>
                  {stat.source && <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginTop: '0.4rem' }}>{stat.source}</div>}
                </div>
              ))}
            </div>
            <div style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.18), rgba(239,68,68,0.16))', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '1.2rem', padding: '1.5rem', boxShadow: '0 18px 48px rgba(0,0,0,0.2)' }}>
              <p style={{ margin: 0, fontSize: '1.1rem', lineHeight: 1.8, color: '#dbeafe' }}>
                “I grew up in Bangladesh — the world’s most flood-exposed country — where emergencies happen without coordination. UrbanNerve is the system I wish existed.”
              </p>
              <div style={{ marginTop: '1rem', color: 'white', fontWeight: 700 }}>— Atiquzzaman Bhuiyan, Founder | New York University</div>
            </div>
          </div>
        </section>

        <section id="about" style={{ background: 'white', padding: '5rem 1.25rem' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '1.2rem', padding: '1.6rem' }}>
              <p style={{ color: '#2563eb', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.6rem' }}>Mission</p>
              <h3 style={{ margin: 0, fontSize: '1.45rem', lineHeight: 1.3 }}>No one should die waiting for help.</h3>
            </div>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '1.2rem', padding: '1.6rem' }}>
              <p style={{ color: '#2563eb', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.6rem' }}>Vision</p>
              <h3 style={{ margin: 0, fontSize: '1.45rem', lineHeight: 1.3 }}>Real-time emergency coordination for every city on earth.</h3>
            </div>
            <div style={{ background: '#0f172a', color: 'white', borderRadius: '1.2rem', padding: '1.6rem' }}>
              <p style={{ color: '#93c5fd', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.6rem' }}>Founder</p>
              <h3 style={{ margin: 0, fontSize: '1.15rem', lineHeight: 1.4 }}>Atiquzzaman Bhuiyan</h3>
              <p style={{ margin: '0.4rem 0 0', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}>Founder &amp; CEO • Mechanical Engineer • New York University</p>
            </div>
          </div>
        </section>

        <section id="partners" style={{ background: '#f8fafc', padding: '5rem 1.25rem' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '1.5rem', alignItems: 'center' }}>
            <div>
              <p style={{ color: '#2563eb', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '0.6rem' }}>Partners</p>
              <h2 style={{ margin: 0, fontSize: 'clamp(2rem, 3vw, 2.7rem)', lineHeight: 1.15, letterSpacing: '-0.025em' }}>Join the pilot program</h2>
              <p style={{ margin: '1rem 0 0', color: '#475569', lineHeight: 1.8, maxWidth: '620px' }}>UrbanNerve is designed for hospitals, ambulance services, and governments that want faster coordination and safer outcomes.</p>
            </div>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {['Hospitals', 'Ambulance Services', 'Governments'].map((partner) => (
                <div key={partner} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '1rem', padding: '1.1rem 1.2rem', fontWeight: 700 }}>{partner}</div>
              ))}
              <a href="mailto:hello@urbannerve.org" style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: 'white', borderRadius: '999px', padding: '0.95rem 1.3rem', textDecoration: 'none', fontWeight: 800, textAlign: 'center', display: 'inline-block' }}>Request a Pilot</a>
            </div>
          </div>
        </section>
      </main>

      <footer style={{ background: '#0f172a', color: 'white', padding: '2rem 1.25rem 2.6rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem' }}>
          <div>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>Company</h3>
            <div style={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.8 }}>
              <div><a href="#about" style={{ color: 'inherit', textDecoration: 'none' }}>About</a></div>
              <div><a href="#impact" style={{ color: 'inherit', textDecoration: 'none' }}>Impact</a></div>
            </div>
          </div>
          <div>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>Product</h3>
            <div style={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.8 }}>
              <div><a href="#how-it-works" style={{ color: 'inherit', textDecoration: 'none' }}>How It Works</a></div>
              <div><a href="#features" style={{ color: 'inherit', textDecoration: 'none' }}>Features</a></div>
              <div><a href="#partners" style={{ color: 'inherit', textDecoration: 'none' }}>Partners</a></div>
            </div>
          </div>
          <div>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>Contact</h3>
            <div style={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.8 }}>
              <div><a href="mailto:hello@urbannerve.org" style={{ color: 'inherit', textDecoration: 'none' }}>hello@urbannerve.org</a></div>
            </div>
          </div>
        </div>
        <div style={{ maxWidth: '1200px', margin: '1.6rem auto 0', borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: '1rem', color: 'rgba(255,255,255,0.64)', fontSize: '0.95rem', lineHeight: 1.7 }}>
          <div>© 2026 UrbanNerve. All rights reserved.</div>
          <div style={{ marginTop: '0.2rem' }}>UrbanNerve is a coordination tool. Always call local emergency services in life-threatening situations.</div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
